import {
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useOnlineStatus } from './useOnlineStatus';
import { getAtuacao } from '@/lib/delegacao/atuacao';
import { addEntry, type OutboxType } from '@/lib/pwa/outbox';
import { toast } from 'sonner';

interface UseOfflineMutationOptions<TData, TVariables> {
  /** Função de mutação normal (usada quando online). */
  mutationFn: (variables: TVariables) => Promise<TData>;
  /** Configuração do outbox (usada quando offline). */
  outbox: {
    type: OutboxType;
    getPayload: (variables: TVariables) => {
      method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
      url: string;
      body?: unknown;
    };
    getDescription: (variables: TVariables) => string;
  };
  /** Callback quando a mutação foi executada com sucesso no servidor (online). */
  onSuccess?: (data: TData, variables: TVariables) => void;
  /** Callback quando a mutação foi enfileirada no outbox (offline). Usar para fechar dialogs/limpar forms. */
  onQueued?: (variables: TVariables) => void;
  /** Callback de erro simplificado. */
  onError?: (error: Error, variables: TVariables) => void;
  /** Query keys a invalidar após sucesso. */
  invalidateKeys?: string[][];
}

/**
 * Hook que enfileira mutações no outbox quando offline,
 * executando-as normalmente quando online.
 *
 * Equivalente Angular/Java: um service que intercepta chamadas HTTP
 * e redireciona para uma fila local quando não há conexão.
 */
export function useOfflineMutation<TData = unknown, TVariables = void>(
  options: UseOfflineMutationOptions<TData, TVariables>,
) {
  const { isOnline } = useOnlineStatus();
  const queryClient = useQueryClient();

  return useMutation<TData, Error, TVariables>({
    mutationFn: async (variables: TVariables) => {
      if (isOnline) {
        return options.mutationFn(variables);
      }

      // Na conta de outra pessoa não se trabalha offline (P11 — pendência).
      if (getAtuacao()) {
        throw new Error('Sem conexão. Na conta de outra pessoa, as ações não ficam salvas offline.');
      }

      // Offline — enfileirar no outbox
      const payload = options.outbox.getPayload(variables);
      const description = options.outbox.getDescription(variables);

      await addEntry({
        type: options.outbox.type,
        payload,
        description,
      });

      toast.info('Ação salva offline', {
        description: `${description} — será sincronizada ao reconectar.`,
      });

      options.onQueued?.(variables);

      return { __offlineQueued: true } as TData;
    },
    onSuccess: (data, variables) => {
      if ((data as Record<string, unknown>)?.__offlineQueued) return;

      // Apenas para respostas reais do servidor (online)
      if (options.invalidateKeys) {
        for (const key of options.invalidateKeys) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }

      options.onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      options.onError?.(error, variables);
    },
  });
}
